/**
 * Page de prévisualisation du template d'email de signature
 * Accessible via /email-preview (pour démonstration)
 */
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Mail, User, Building2, Phone, FileSignature } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Logo Valnexo officiel (hébergé publiquement)
const VALNEXO_LOGO_URL = "https://customer-assets.emergentagent.com/job_2fa003f4-c4fb-4906-a1a2-86d7ed54f349/artifacts/2y9b9qoi_logo%20valnexo%20vectorise%CC%81.png";

export default function EmailPreview() {
    const navigate = useNavigate();
    
    // Données exemple pour la prévisualisation
    const senderInfo = {
        name: "Morgan Dupont",
        email: "morgan@valnexo.fr",
        phone: "06 12 34 56 78",
        role: "Conseiller Énergie"
    };
    
    const recipientInfo = {
        name: "Jean Martin",
        email: "jean.martin@example.com",
        company: "SCI Les Oliviers"
    };
    
    const projectInfo = {
        type: "Rénovation globale d'ampleur",
        address: "12 rue des Lilas, 75001 Paris"
    };

    return (
        <div className="min-h-screen bg-slate-100 p-8">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <Button variant="outline" onClick={() => navigate(-1)}>
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Retour
                    </Button>
                    <h1 className="text-xl font-bold text-slate-800">
                        Prévisualisation Email de Signature
                    </h1>
                </div>
                
                {/* Email Client Mockup */}
                <Card className="shadow-xl overflow-hidden">
                    {/* Email Header Bar */}
                    <div className="bg-slate-800 text-white p-4">
                        <div className="flex items-center gap-3 mb-3">
                            <Mail className="w-5 h-5" />
                            <span className="font-medium">Nouveau message</span>
                        </div>
                        <div className="space-y-2 text-sm">
                            <div className="flex items-center gap-2">
                                <span className="text-slate-400 w-16">De:</span>
                                <span className="font-medium">{senderInfo.name} &lt;{senderInfo.email}&gt;</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-slate-400 w-16">À:</span>
                                <span>{recipientInfo.name} &lt;{recipientInfo.email}&gt;</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-slate-400 w-16">Objet:</span>
                                <span className="font-medium">Votre devis de rénovation énergétique - Signature requise</span>
                            </div>
                        </div>
                    </div>
                    
                    {/* Email Body */}
                    <CardContent className="p-0">
                        <div className="bg-white">
                            {/* Email Content */}
                            <div style={{
                                fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                                maxWidth: "600px",
                                margin: "0 auto",
                                backgroundColor: "#ffffff"
                            }}>
                                {/* Header with Logo */}
                                <div style={{
                                    backgroundColor: "#0B1F3B",
                                    padding: "24px 32px",
                                    textAlign: "center"
                                }}>
                                    <img 
                                        src={VALNEXO_LOGO_URL}
                                        alt="Valnexo"
                                        style={{ height: "70px", margin: "0 auto" }}
                                    />
                                </div>
                                
                                {/* Main Content */}
                                <div style={{ padding: "32px" }}>
                                    <h1 style={{
                                        color: "#0B1F3B",
                                        fontSize: "24px",
                                        fontWeight: "600",
                                        marginBottom: "24px",
                                        borderBottom: "3px solid #F2B705",
                                        paddingBottom: "12px"
                                    }}>
                                        Votre devis est prêt pour signature
                                    </h1>
                                    
                                    <p style={{ color: "#374151", fontSize: "16px", lineHeight: "1.6", marginBottom: "16px" }}>
                                        Bonjour <strong>{recipientInfo.name}</strong>,
                                    </p>
                                    
                                    <p style={{ color: "#374151", fontSize: "16px", lineHeight: "1.6", marginBottom: "16px" }}>
                                        Suite à notre échange, vous trouverez ci-joint le devis pour les travaux de rénovation énergétique de votre bien.
                                    </p>
                                    
                                    <p style={{ color: "#374151", fontSize: "16px", lineHeight: "1.6", marginBottom: "24px" }}>
                                        Ce document détaille l'ensemble des travaux prévus ainsi que les aides auxquelles vous êtes éligible.
                                    </p>
                                    
                                    {/* Project Info Box */}
                                    <div style={{
                                        backgroundColor: "#F8FAFC",
                                        border: "1px solid #E2E8F0",
                                        borderRadius: "8px",
                                        padding: "20px",
                                        marginBottom: "24px"
                                    }}>
                                        <h3 style={{ color: "#0B1F3B", fontSize: "14px", fontWeight: "600", marginBottom: "12px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                            Récapitulatif du projet
                                        </h3>
                                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                                <span style={{ color: "#64748B", fontSize: "14px", width: "80px" }}>Client:</span>
                                                <span style={{ color: "#0B1F3B", fontSize: "14px", fontWeight: "500" }}>{recipientInfo.company}</span>
                                            </div>
                                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                                <span style={{ color: "#64748B", fontSize: "14px", width: "80px" }}>Type:</span>
                                                <span style={{ color: "#0B1F3B", fontSize: "14px", fontWeight: "500" }}>{projectInfo.type}</span>
                                            </div>
                                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                                <span style={{ color: "#64748B", fontSize: "14px", width: "80px" }}>Adresse:</span>
                                                <span style={{ color: "#0B1F3B", fontSize: "14px", fontWeight: "500" }}>{projectInfo.address}</span>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* CTA Button */}
                                    <div style={{ textAlign: "center", marginBottom: "24px" }}>
                                        <a 
                                            href="#"
                                            style={{
                                                display: "inline-block",
                                                backgroundColor: "#1E5BFF",
                                                color: "#ffffff",
                                                padding: "16px 48px",
                                                borderRadius: "8px",
                                                textDecoration: "none",
                                                fontWeight: "600",
                                                fontSize: "16px",
                                                boxShadow: "0 4px 14px rgba(30, 91, 255, 0.3)"
                                            }}
                                        >
                                            <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                                                ✍️ Signer le devis
                                            </span>
                                        </a>
                                    </div>
                                    
                                    <p style={{ color: "#64748B", fontSize: "14px", textAlign: "center", marginBottom: "24px" }}>
                                        Ce lien de signature est valable pendant 14 jours.
                                    </p>
                                    
                                    <p style={{ color: "#374151", fontSize: "16px", lineHeight: "1.6", marginBottom: "8px" }}>
                                        Pour toute question, n'hésitez pas à nous contacter.
                                    </p>
                                    
                                    <p style={{ color: "#374151", fontSize: "16px", lineHeight: "1.6" }}>
                                        Cordialement,
                                    </p>
                                </div>
                                
                                {/* Sender Signature */}
                                <div style={{
                                    backgroundColor: "#F8FAFC",
                                    borderTop: "1px solid #E2E8F0",
                                    padding: "24px 32px"
                                }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                                        <div style={{
                                            width: "56px",
                                            height: "56px",
                                            borderRadius: "50%",
                                            backgroundColor: "#1E5BFF",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            color: "#ffffff",
                                            fontWeight: "600",
                                            fontSize: "20px"
                                        }}>
                                            {senderInfo.name.split(' ').map(n => n[0]).join('')}
                                        </div>
                                        <div>
                                            <div style={{ color: "#0B1F3B", fontWeight: "600", fontSize: "16px" }}>
                                                {senderInfo.name}
                                            </div>
                                            <div style={{ color: "#64748B", fontSize: "14px" }}>
                                                {senderInfo.role} • Valnexo
                                            </div>
                                            <div style={{ color: "#1E5BFF", fontSize: "14px", marginTop: "4px" }}>
                                                📧 {senderInfo.email} • 📞 {senderInfo.phone}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Footer */}
                                <div style={{
                                    backgroundColor: "#0B1F3B",
                                    padding: "20px 32px",
                                    textAlign: "center"
                                }}>
                                    <p style={{ color: "#94A3B8", fontSize: "12px", marginBottom: "8px" }}>
                                        Valnexo • CRM Énergie
                                    </p>
                                    <p style={{ color: "#64748B", fontSize: "11px", marginBottom: "4px" }}>
                                        Cet email a été envoyé automatiquement via la plateforme Valnexo.
                                    </p>
                                    <p style={{ color: "#64748B", fontSize: "11px" }}>
                                        © 2026 Valnexo. Tous droits réservés.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                
                {/* Info Box */}
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h3 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        Informations sur l'envoi
                    </h3>
                    <ul className="text-sm text-blue-700 space-y-1">
                        <li>• L'email sera envoyé depuis l'adresse de l'utilisateur CRM connecté</li>
                        <li>• Le logo Valnexo officiel sera inclus dans l'en-tête</li>
                        <li>• Le lien de signature sera généré par Yousign (signature électronique légale)</li>
                        <li>• Le statut de signature sera mis à jour automatiquement dans le CRM</li>
                    </ul>
                </div>
            </div>
        </div>
    );
}
